import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, ReferenceLine } from "recharts";
import { Sparkles, TrendingUp, TrendingDown, Minus, ArrowLeft, RefreshCw, Users, ChevronDown, ChevronUp } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "../lib/api";
import { useLang } from "../lib/i18n";
import { useAuth } from "../context/AuthContext";
import { StatusBadge, SeverityBadge, healthColor } from "../components/badges";
import ProjectMembersDialog from "../components/ProjectMembersDialog";
import ProjectTabs, { useWorkSummary } from "../components/work/ProjectTabs";
import UpdateReactions from "../components/UpdateReactions";

function ReporterRow({ r, canReact, onChanged }) {
  const { t } = useLang();
  const [open, setOpen] = useState(false);
  const submitted = r.update && r.update.status === "SUBMITTED";
  return (
    <div className="text-sm border-b last:border-0 pb-2 last:pb-0" data-testid={`reporter-row-${r.user_id}`}>
      <div className="flex items-center justify-between gap-2">
        <div className="min-w-0">
          <div className="font-medium">{r.name}</div>
          <div className="text-[11px] text-muted-foreground truncate">{r.reason}</div>
        </div>
        <div className="flex items-center gap-1.5">
          <StatusBadge status={r.status} />
          {submitted && (
            <button type="button" data-testid={`reporter-toggle-${r.user_id}`} onClick={() => setOpen(!open)}
              className="p-1 rounded hover:bg-secondary text-muted-foreground" title={open ? t("hideUpdate") : t("showUpdate")}>
              {open ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
            </button>
          )}
        </div>
      </div>
      {submitted && open && (
        <div className="mt-2 rounded-md bg-secondary/40 p-2.5 space-y-1" data-testid={`reporter-update-${r.user_id}`}>
          <div className="text-xs"><span className="font-medium">{t("completedOutcomes")}: </span>{r.update.completed_summary || "—"}</div>
          <div className="text-xs"><span className="font-medium">{t("todayPriorities")}: </span>{r.update.next_summary || "—"}</div>
          {r.update.blocker_flag && (
            <div className="text-xs text-[#991B1B]"><span className="font-medium">{t("blockerFlag")}: </span>{r.update.blocker_details || "—"}</div>
          )}
          <UpdateReactions update={r.update} canReact={canReact} onChanged={onChanged} idSuffix={r.user_id} />
        </div>
      )}
    </div>
  );
}
import { Button } from "../components/ui/button";
import { Skeleton } from "../components/ui/skeleton";

function TrendIcon({ trend }) {
  if (trend === "IMPROVING") return <TrendingUp className="h-4 w-4 text-[#16A34A]" />;
  if (trend === "WORSENING") return <TrendingDown className="h-4 w-4 text-[#DC2626]" />;
  return <Minus className="h-4 w-4 text-muted-foreground" />;
}

function ListBlock({ title, items, testId }) {
  if (!items || items.length === 0) return null;
  return (
    <div data-testid={testId}>
      <div className="text-xs font-semibold tracking-wide text-muted-foreground uppercase mb-1">{title}</div>
      <ul className="space-y-0.5">
        {items.map((x) => {
          const text = typeof x === "string" ? x : x.action || JSON.stringify(x);
          return (
            <li key={text} className="text-sm flex gap-1.5">
              <span className="text-[#1E3A8A]">•</span>
              <span>{text}</span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

export default function ProjectDashboard() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const { t, lang } = useLang();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [burndown, setBurndown] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [membersOpen, setMembersOpen] = useState(false);
  const { summary: workSummary, sprints, reload: reloadWork } = useWorkSummary(projectId);

  const load = useCallback(() => {
    api.get(`/projects/${projectId}/dashboard`, { params: { language: lang } })
      .then((r) => setData(r.data))
      .catch((e) => toast.error(apiError(e)));
  }, [projectId, lang]);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    api.get(`/projects/${projectId}/burndown`)
      .then((r) => setBurndown(r.data))
      .catch(() => setBurndown({ available: false }));
  }, [projectId]);

  if (!data)
    return <div className="space-y-4"><Skeleton className="h-10 w-64" /><Skeleton className="h-60 w-full" /></div>;

  const { project, health, summary, warnings, reporters, dependencies, jira_movement, trend, interventions, ai_suggestions } = data;
  const sj = summary.summary_json || {};

  const generateAI = async () => {
    setGenerating(true);
    try {
      await api.post(`/projects/${projectId}/summary/generate`, { language: lang });
      toast.success(t("morningBrief") + " ✓");
      load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setGenerating(false);
    }
  };

  const act = async (fn, msg) => {
    try { await fn(); toast.success(msg); load(); } catch (e) { toast.error(apiError(e)); }
  };
  const canManage = user.role === "ADMIN" ||
    (user.role === "PM" && [project.project_manager_id, project.scrum_master_id].includes(user.id));

  return (
    <div className="space-y-6" data-testid="project-dashboard-page">
      {workSummary ? (
        <ProjectTabs summary={workSummary} sprints={sprints} onCreated={() => { reloadWork(); load(); }}>
          {canManage && (
            <Button size="sm" variant="outline" data-testid="manage-team-btn" onClick={() => setMembersOpen(true)}>
              <Users className="h-3.5 w-3.5 mr-1.5" />{t("manageMembers")}
            </Button>
          )}
        </ProjectTabs>
      ) : (
        <div className="flex items-center justify-between gap-3">
          <button onClick={() => navigate("/projects")} className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors">
            <ArrowLeft className="h-4 w-4" /> {t("projects")}
          </button>
          {canManage && (
            <Button size="sm" variant="outline" data-testid="manage-team-btn" onClick={() => setMembersOpen(true)}>
              <Users className="h-3.5 w-3.5 mr-1.5" />{t("manageMembers")}
            </Button>
          )}
        </div>
      )}
      <ProjectMembersDialog project={project} open={membersOpen} onOpenChange={setMembersOpen} onChanged={load} />

      <div className="apha-card p-5">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <div className="text-xs text-muted-foreground">{project.key} · {project.client_name} · PM: {project.pm_name}</div>
            <h1 className="font-heading text-2xl font-semibold">{project.name}</h1>
            <div className="flex items-center gap-2 mt-2">
              <StatusBadge status={health.status} testId="dashboard-health-status" />
              <TrendIcon trend={health.trend} />
              <span className="text-xs text-muted-foreground">
                {t("evidenceConfidence")}: {Math.round(health.confidence * 100)}% · {data.jira_mode === "REAL" ? t("liveJira") : t("evidenceBoard")}
              </span>
            </div>
          </div>
          <div className="flex gap-6 text-center">
            <div>
              <div className="text-3xl font-heading font-semibold" style={{ color: healthColor(health.status) }} data-testid="dashboard-health-score">
                {health.score}
              </div>
              <div className="text-[11px] text-muted-foreground">{t("healthScore")}</div>
            </div>
            <div>
              <div className="text-3xl font-heading font-semibold">{health.coverage.percentage}%</div>
              <div className="text-[11px] text-muted-foreground">
                {t("coverage")} ({health.coverage.submitted}/{health.coverage.expected})
              </div>
            </div>
          </div>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mt-5">
          {Object.entries(health.dimensions).map(([k, v]) => (
            <div key={k}>
              <div className="flex justify-between text-[11px] text-muted-foreground mb-1">
                <span className="capitalize">{k}</span><span>{v}</span>
              </div>
              <div className="h-1.5 rounded-full bg-secondary overflow-hidden">
                <div className="h-full rounded-full" style={{ width: `${v}%`, backgroundColor: v >= 70 ? "#16A34A" : v >= 50 ? "#D97706" : "#DC2626" }} />
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-5">
        <div className="lg:col-span-2 space-y-5">
          <section className="apha-card p-5" data-testid="morning-brief-card">
            <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
              <h2 className="font-heading font-semibold text-base flex items-center gap-2">
                <Sparkles className="h-4 w-4 text-[#3730A3]" /> {t("morningBrief")}
              </h2>
              <div className="flex items-center gap-2">
                <span className={`text-[10px] px-1.5 py-0.5 rounded border font-medium ${
                  summary.generated_by === "AI" ? "bg-[#E0E7FF] text-[#3730A3] border-[#C7D2FE]" : "bg-secondary text-muted-foreground"
                }`} data-testid="summary-source-badge">
                  {summary.generated_by === "AI" ? `AI · ${summary.model_version}` : t("rulesOnly")}
                </span>
                <Button size="sm" variant="outline" data-testid="generate-ai-brief-btn" disabled={generating} onClick={generateAI}>
                  <RefreshCw className={`h-3.5 w-3.5 mr-1.5 ${generating ? "animate-spin" : ""}`} />
                  {generating ? t("generating") : t("generateAiBrief")}
                </Button>
              </div>
            </div>
            <p className="text-sm font-medium mb-4" data-testid="summary-headline">{summary.headline}</p>
            <div className="grid sm:grid-cols-2 gap-4">
              <ListBlock title={t("completedOutcomes")} items={sj.completed_outcomes} testId="brief-completed" />
              <ListBlock title={t("todayPriorities")} items={sj.today_priorities} testId="brief-priorities" />
              <ListBlock title={t("activeBlockers")} items={sj.blockers} testId="brief-blockers" />
              <ListBlock title={t("dependencies")} items={sj.dependencies} testId="brief-dependencies" />
              <ListBlock title={t("decisionsNeeded")} items={sj.decisions_needed} testId="brief-decisions" />
              <ListBlock title={t("capacityConcerns")} items={sj.capacity_conflicts} testId="brief-capacity" />
            </div>
            {sj.missing_reporter_context && (
              <div className="mt-3 text-xs bg-[#FEF3C7] border border-[#FDE68A] text-[#92400E] rounded px-2.5 py-1.5">
                {sj.missing_reporter_context}
              </div>
            )}
            {sj.recommended_actions?.length > 0 && (
              <div className="mt-4">
                <div className="text-xs font-semibold tracking-wide text-muted-foreground uppercase mb-1.5">{t("recommendedActions")}</div>
                {sj.recommended_actions.map((a) => (
                  <div key={a.action} className="text-sm border-l-2 border-[#1E3A8A] pl-2.5 py-1 mb-1">
                    {a.action}
                    <span className="text-xs text-muted-foreground"> — {a.owner_role} · {a.target_timing}</span>
                  </div>
                ))}
              </div>
            )}
          </section>

          <section className="apha-card p-5" data-testid="dashboard-warnings-section">
            <h2 className="font-heading font-semibold text-base mb-3">{t("warnings")} ({warnings.length})</h2>
            <div className="space-y-2">
              {warnings.map((w) => (
                <div key={w.id} className="border rounded-md p-3">
                  <div className="flex flex-wrap items-center gap-2">
                    <SeverityBadge severity={w.severity} />
                    <StatusBadge status={w.status} />
                    <span className="text-xs text-muted-foreground">{w.category} · {w.rule_id || w.source} · {Math.round(w.age_hours || 0)}h</span>
                  </div>
                  <div className="text-sm mt-1.5">{w.summary}</div>
                  {w.evidence?.slice(0, 2).map((ev) => (
                    <div key={`${ev.source_id}-${ev.fact}`} className="text-xs text-muted-foreground mt-0.5">• {ev.source_id}: {ev.fact}</div>
                  ))}
                </div>
              ))}
              {warnings.length === 0 && <div className="text-sm text-muted-foreground">{t("noData")}</div>}
            </div>
          </section>

          <section className="apha-card p-5" data-testid="jira-movement-section">
            <h2 className="font-heading font-semibold text-base mb-3">{t("jiraMovement")}</h2>
            <div className="grid sm:grid-cols-3 gap-4">
              {[["doneRecently", jira_movement.done_recently], ["inProgress", jira_movement.in_progress], ["staleItems", jira_movement.stale]].map(([key, list]) => (
                <div key={key}>
                  <div className="text-xs font-semibold tracking-wide text-muted-foreground uppercase mb-1.5">{t(key)}</div>
                  {list.map((i) => (
                    <div key={i.issue_key} className="text-xs py-1 border-b last:border-0">
                      <span className="font-medium">{i.issue_key}</span> {i.summary}
                      <span className="text-muted-foreground"> · {i.priority}</span>
                    </div>
                  ))}
                  {list.length === 0 && <div className="text-xs text-muted-foreground">—</div>}
                </div>
              ))}
            </div>
          </section>
        </div>

        <div className="space-y-5">
          <section className="apha-card p-5" data-testid="health-trend-card">
            <h2 className="font-heading font-semibold text-base mb-2">{t("healthTrend")}</h2>
            <div className="h-40">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={trend.map((s) => ({ date: s.date.slice(5), score: s.score }))}>
                  <XAxis dataKey="date" tick={{ fontSize: 10 }} />
                  <YAxis domain={[0, 100]} tick={{ fontSize: 10 }} width={28} />
                  <Tooltip />
                  <ReferenceLine y={70} stroke="#D97706" strokeDasharray="4 4" />
                  <ReferenceLine y={50} stroke="#DC2626" strokeDasharray="4 4" />
                  <Line type="monotone" dataKey="score" stroke="#1E3A8A" strokeWidth={2} dot={{ r: 2 }} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </section>

          <section className="apha-card p-5" data-testid="burndown-card">
            <div className="flex items-center justify-between gap-2 mb-2">
              <h2 className="font-heading font-semibold text-base">{t("sprintBurndown")}</h2>
              {burndown?.available && (
                <span className={`text-[10px] font-semibold border rounded px-1.5 py-0.5 ${
                  burndown.mode === "REAL" ? "text-[#166534] bg-[#DCFCE7] border-[#BBF7D0]"
                    : "text-[#334155] bg-[#F1F5F9] border-[#E2E8F0]"}`} data-testid="burndown-mode-badge">
                  {burndown.mode === "REAL" ? t("liveJira") : burndown.mode === "NATIVE" ? `${t("nativeBoard")} · ${burndown.sprint?.name || ""}` : t("mockJira")}
                </span>
              )}
            </div>
            {!burndown ? (
              <Skeleton className="h-40 w-full" />
            ) : !burndown.available ? (
              <div className="text-sm text-muted-foreground" data-testid="burndown-unavailable">
                {burndown.reason || t("noData")}
              </div>
            ) : (
              <>
                <div className="text-[11px] text-muted-foreground mb-2" data-testid="burndown-sprint-info">
                  {burndown.sprint.name} · {burndown.sprint.start_date} → {burndown.sprint.end_date}
                  <span className="font-medium text-foreground"> · {burndown.remaining}/{burndown.total} </span>
                  {burndown.unit === "points" ? t("storyPoints") : t("issuesLabel")} {t("remainingLabel")}
                </div>
                <div className="h-40">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={burndown.series.map((s) => ({ ...s, date: s.date.slice(5) }))}>
                      <XAxis dataKey="date" tick={{ fontSize: 10 }} />
                      <YAxis tick={{ fontSize: 10 }} width={28} allowDecimals={false} />
                      <Tooltip />
                      <Line type="monotone" dataKey="ideal" name={t("idealLine")} stroke="#94A3B8"
                        strokeWidth={1.5} strokeDasharray="5 4" dot={false} />
                      <Line type="monotone" dataKey="remaining" name={t("remainingSeries")} stroke="#1E3A8A"
                        strokeWidth={2} dot={{ r: 2 }} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </>
            )}
          </section>

          <section className="apha-card p-5" data-testid="reporters-section">
            <h2 className="font-heading font-semibold text-base mb-3">{t("reporters")}</h2>
            <div className="space-y-2">
              {reporters.map((r) => (
                <ReporterRow key={r.user_id} r={r} canReact={["PM", "LEAD", "ADMIN"].includes(user.role)} onChanged={load} />
              ))}
              {reporters.length === 0 && <div className="text-sm text-muted-foreground">{t("noData")}</div>}
            </div>
          </section>

          <section className="apha-card p-5" data-testid="dependencies-section">
            <h2 className="font-heading font-semibold text-base mb-3">{t("dependencies")}</h2>
            <div className="space-y-2">
              {dependencies.map((d) => (
                <div key={d.id} className="text-sm border rounded-md p-2.5">
                  <div>{d.description}</div>
                  <div className="text-[11px] text-muted-foreground mt-0.5">
                    {d.dependency_type} · {t("owner")}: {d.owner_name || "—"} · {t("depNeededBy")}: {d.needed_by || "—"}
                  </div>
                </div>
              ))}
              {dependencies.length === 0 && <div className="text-sm text-muted-foreground">{t("noData")}</div>}
            </div>
          </section>

          <section className="apha-card p-5" data-testid="interventions-section">
            <h2 className="font-heading font-semibold text-base mb-3">{t("interventionTracking")}</h2>
            <div className="space-y-2">
              {interventions.map((i) => (
                <div key={i.id} className="text-sm border rounded-md p-2.5">
                  <div>{i.action}</div>
                  <div className="text-[11px] text-muted-foreground mt-0.5">
                    {t("owner")}: {i.owner_name} · {t("targetDate")}: {(i.target_at || "").slice(0, 10) || "—"}
                    {i.escalated_at && <span className="text-[#991B1B]"> · {t("overdueInterventions")}</span>}
                  </div>
                  <Button size="sm" variant="outline" className="mt-1.5 h-7 text-xs"
                    data-testid={`dash-complete-int-${i.id}`}
                    onClick={() => act(() => api.post(`/interventions/${i.id}/complete`), t("complete"))}>
                    {t("complete")}
                  </Button>
                </div>
              ))}
              {interventions.length === 0 && <div className="text-sm text-muted-foreground">{t("noData")}</div>}
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}
